import { Renderer2 } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FILE_VALIDATION_CONSTANTS } from '../constants/file-validation.constants';
import { DragValidationResult } from '../services/file-validation.service';

/**
 * Utility class for drag and drop visual state management
 */
export class DragDropVisualUtils {

  /**
   * Updates CSS classes on an element based on drag validation result
   */
  static updateElementClasses(
    element: HTMLElement,
    renderer: Renderer2,
    validation: DragValidationResult
  ): void {
    const { CSS_CLASSES } = FILE_VALIDATION_CONSTANTS;

    renderer.removeClass(element, CSS_CLASSES.DRAG_VALID);
    renderer.removeClass(element, CSS_CLASSES.DRAG_INVALID);
    renderer.removeClass(element, CSS_CLASSES.DRAG_MIXED);

    if (validation.hasMixed) {
      renderer.addClass(element, CSS_CLASSES.DRAG_MIXED);
    } else if (validation.isValid) {
      renderer.addClass(element, CSS_CLASSES.DRAG_VALID);
    } else {
      renderer.addClass(element, CSS_CLASSES.DRAG_INVALID);
    }
  }

  /**
   * Updates element classes and message based on drag validation result
   */
  static updateElementClassesWithMessage(
    element: HTMLElement,
    renderer: Renderer2,
    validation: DragValidationResult,
    translateService: any
  ): void {
    this.updateElementClasses(element, renderer, validation);

    const message = this.getDragMessage(validation, translateService);
    renderer.setAttribute(element, 'data-drag-message', message);
  }

  /**
   * Removes all drag-related CSS classes from an element
   */
  static resetElementClasses(element: HTMLElement, renderer: Renderer2): void {
    const { CSS_CLASSES } = FILE_VALIDATION_CONSTANTS;

    renderer.removeClass(element, CSS_CLASSES.DRAG_VALID);
    renderer.removeClass(element, CSS_CLASSES.DRAG_INVALID);
    renderer.removeClass(element, CSS_CLASSES.DRAG_MIXED);
    renderer.removeAttribute(element, 'data-drag-message');
  }

  /**
   * Gets the appropriate message for drag validation state
   */
  static getDragMessage(
    validation: DragValidationResult,
    translateService: TranslateService
  ): string {
    if (validation.exceedsMaxFiles || validation.invalidReason === 'count') {
      return translateService.instant(FILE_VALIDATION_CONSTANTS.DRAG_MESSAGES.UPLOAD_SINGLE_FILE);
    } else if (validation.hasMixed) {
      return translateService.instant(FILE_VALIDATION_CONSTANTS.DRAG_MESSAGES.SOME_FILES_WILL_BE_SKIPPED);
    } else if (validation.isValid) {
      return translateService.instant(FILE_VALIDATION_CONSTANTS.DRAG_MESSAGES.DRAG_OR_PASTE_FILES_HERE);
    } else {
      if (validation.invalidReason === 'size') {
        return translateService.instant(FILE_VALIDATION_CONSTANTS.DRAG_MESSAGES.FILE_TOO_LARGE);
      } else if (validation.invalidReason === 'type') {
        return translateService.instant(FILE_VALIDATION_CONSTANTS.DRAG_MESSAGES.UNSUPPORTED_FILE_TYPE);
      } else {
        return translateService.instant(FILE_VALIDATION_CONSTANTS.DRAG_MESSAGES.FILE_NOT_SUPPORTED);
      }
    }
  }
}

/**
 * Utility class for overlay management in full-page drag drop
 */
export class DragDropOverlayUtils {

  /**
   * Updates overlay visual state based on validation result
   */
  static updateOverlayVisualState(
    overlay: HTMLElement,
    renderer: Renderer2,
    validation: DragValidationResult
  ): void {
    const { COLORS } = FILE_VALIDATION_CONSTANTS;

    let backgroundColor: string;
    let borderColor: string;

    if (validation.hasMixed) {
      backgroundColor = COLORS.MIXED.BACKGROUND;
      borderColor = COLORS.MIXED.BORDER;
    } else if (validation.isValid) {
      backgroundColor = COLORS.VALID.BACKGROUND;
      borderColor = COLORS.VALID.BORDER;
    } else {
      backgroundColor = COLORS.INVALID.BACKGROUND;
      borderColor = COLORS.INVALID.BORDER;
    }

    renderer.setStyle(overlay, 'background-color', backgroundColor);
    renderer.setStyle(overlay, 'border', `5px solid ${borderColor}`);
  }

  /**
   * Updates overlay text content
   */
  static updateOverlayText(
    overlay: HTMLElement,
    renderer: Renderer2,
    message: string
  ): void {
    const textElement = overlay.querySelector('div:last-child');
    if (textElement) {
      renderer.setProperty(textElement, 'textContent', message);
    }
  }

  /**
   * Creates overlay icon element
   */
  static createOverlayIcon(renderer: Renderer2): HTMLElement {
    const icon = renderer.createElement('span');
    renderer.addClass(icon, 'material-symbols-outlined');
    renderer.setProperty(icon, 'textContent', 'cloud_upload');
    renderer.setStyle(icon, 'font-size', '48px');
    renderer.setStyle(icon, 'color', 'var(--wt-black)');
    renderer.setStyle(icon, 'margin-bottom', '16px');
    return icon;
  }

  /**
   * Creates overlay text element
   */
  static createOverlayText(renderer: Renderer2, message: string): HTMLElement {
    const mainText = renderer.createElement('div');
    renderer.setStyle(mainText, 'font-size', '16px');
    renderer.setStyle(mainText, 'font-weight', '600');
    renderer.setStyle(mainText, 'color', 'var(--wt-black)');
    renderer.setStyle(mainText, 'line-height', '1');
    renderer.setProperty(mainText, 'textContent', message);
    return mainText;
  }
}

/**
 * Utility class for drag counter management
 */
export class DragCounterUtils {

  /**
   * Manages drag counter for nested drag events
   */
  static manageDragCounter(
    currentCounter: number,
    isEntering: boolean
  ): { newCounter: number; shouldShowOverlay: boolean; shouldHideOverlay: boolean } {
    const newCounter = isEntering ? currentCounter + 1 : currentCounter - 1;

    return {
      newCounter,
      shouldShowOverlay: isEntering && newCounter === 1,
      shouldHideOverlay: !isEntering && newCounter === 0
    };
  }
}
